import { Helmet } from 'react-helmet-async'
import { PRICING_PLANS, formatPrice } from '../data/pricing'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string[]
  canonical?: string
  ogImage?: string
  ogType?: string
  twitterCard?: string
  structuredData?: any
  noIndex?: boolean
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
    section?: string
    tags?: string[]
  }
  breadcrumbs?: Array<{
    name: string
    url: string
  }>
  faq?: Array<{
    question: string
    answer: string
  }>
  localBusiness?: {
    name: string
    address: string
    phone?: string
    priceRange?: string
  }
}

export function SEO({
  title = 'CMMC Level 2 Workbench for Registered Practitioners | Level2Logic',
  // Says what the code does, for the customer the product is actually for.
  // The previous default pitched savings measured against hiring a consultant —
  // to an audience of consultants — and contradicted the landing hero above it.
  description = 'SPRS scoring per DoD Assessment Methodology v1.2.1, SSP and POA&M generation, and portfolio tracking for consultants running CMMC Level 2 self-assessments.',
  keywords = [
    'CMMC compliance',
    'CMMC 2.0',
    'DoD contractor compliance',
    'CMMC Level 2',
    'cybersecurity compliance',
    'defense contractor',
    'CUI protection',
    'NIST 800-171',
    'C3PAO assessment',
    'compliance software',
    'CMMC assessment tool',
    'defense cybersecurity',
    'contractor compliance platform',
    'CMMC self-assessment',
    'cybersecurity framework',
    'DoD cybersecurity',
    'contractor security',
    'CMMC certification',
    'compliance management',
    'cybersecurity assessment'
  ],
  canonical,
  ogImage = '/og-image.svg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  noIndex = false,
  article,
  breadcrumbs,
  faq,
  localBusiness
}: SEOProps) {
  const baseUrl = 'https://level2logic.com'
  const currentUrl = typeof window !== 'undefined' ? window.location.href : baseUrl
  const fullCanonical = canonical || currentUrl

  const fullTitle = title.includes('CMMC') ? title : `${title} | CMMC Compliance Platform`
  const fullDescription = description.length > 160 ? description.substring(0, 157) + '...' : description

  // Enhanced keywords with long-tail variations
  const enhancedKeywords = [
    ...keywords,
    'CMMC compliance software',
    'DoD contractor compliance platform',
    'cybersecurity compliance tool',
    'C3PAO audit preparation',
    'NIST 800-171 compliance',
    'defense contractor cybersecurity',
    'CMMC Level 2 assessment',
    'federal contractor compliance',
    'cybersecurity maturity model certification'
  ]

  // Generate comprehensive structured data
  const generateStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Level2Logic CMMC Platform",
      "url": baseUrl,
      "logo": `${baseUrl}/logo.png`,
      "description": "Leading CMMC 2.0 compliance platform for DoD contractors",
      "foundingDate": "2024",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": "English"
      },
      "sameAs": [
        "https://linkedin.com/company/cmmc-compliance-platform",
        "https://twitter.com/cmmcplatform"
      ]
    }

    if (article) {
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": description,
        "author": {
          "@type": "Organization",
          "name": "CMMC Compliance Platform"
        },
        "publisher": baseData,
        "datePublished": article.publishedTime,
        "dateModified": article.modifiedTime || article.publishedTime,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": fullCanonical
        },
        "articleSection": article.section || "Technology"
      }
    }

    if (faq && faq.length > 0) {
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faq.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      }
    }

    if (localBusiness) {
      return {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": localBusiness.name,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": localBusiness.address
        },
        "telephone": localBusiness.phone,
        "priceRange": localBusiness.priceRange
      }
    }

    // Default to software application schema
    return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Level2Logic CMMC Platform",
      "description": "Complete CMMC 2.0 compliance solution for DoD contractors",
      "url": baseUrl,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      // The public ladder (ADR-0029), from the same data the pricing pages render.
      "offers": PRICING_PLANS.map((plan) => ({
        "@type": "Offer",
        "name": plan.name,
        "price": String(plan.priceMonthly),
        "priceCurrency": "USD",
        "description": `${formatPrice(plan.priceMonthly)}/month ${plan.name} plan, up to ${plan.maxClients} clients`
      })),
      "featureList": [
        "CMMC Level 2 Assessment (110 practices)",
        "SPRS Scoring (DoD Assessment Methodology v1.2.1)",
        "Gap Analysis",
        // Name the documents rather than implying a certification outcome: we
        // do not certify anyone. See the 'Don't' list in COMPANY-SOUL §4.
        "SSP, POA&M and Affirmation Evidence Pack",
        "Multi-Client Portfolio Dashboard",
        "CSV/PDF Export"
      ],
      "screenshot": `${baseUrl}/screenshot.png`,
      "author": baseData,
      "publisher": baseData
    }
  }

  const finalStructuredData = structuredData || generateStructuredData()

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={enhancedKeywords.slice(0, 20).join(', ')} />
      <meta name="author" content="CMMC Compliance Platform" />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1'} />
      <meta name="googlebot" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />

      {/* Canonical and Alternate URLs */}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={`${baseUrl}${ogImage}`} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="CMMC Compliance Platform" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={`${baseUrl}${ogImage}`} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@CMMCPlatform" />
      <meta name="twitter:creator" content="@CMMCPlatform" />

      {/* Additional SEO Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="HandheldFriendly" content="true" />
      <meta name="MobileOptimized" content="width" />

      {/* Search Engine Verification — set via env vars */}
      {import.meta.env.VITE_GOOGLE_VERIFICATION && (
        <meta name="google-site-verification" content={import.meta.env.VITE_GOOGLE_VERIFICATION} />
      )}
      {import.meta.env.VITE_BING_VERIFICATION && (
        <meta name="msvalidate.01" content={import.meta.env.VITE_BING_VERIFICATION} />
      )}
      {import.meta.env.VITE_YANDEX_VERIFICATION && (
        <meta name="yandex-verification" content={import.meta.env.VITE_YANDEX_VERIFICATION} />
      )}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>

      {/* Breadcrumb Structured Data */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.name,
              "item": crumb.url
            }))
          })}
        </script>
      )}

      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />

      {/* Performance and Security */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
    </Helmet>
  )
}
